import React from 'react';
import { SubscriptionStatus } from '@prisma/client';
import useGetSubscription from './useGetSubscription';
import { Feature } from '../../types/subscriptions';
import {
  FEATURE_MINIMUM_TIER,
  FEATURE_PLAN_TYPE,
} from '../../constants/subscriptions';

type FeatureGuard<T extends Feature | null> = T extends null
  ? (feature: Feature) => boolean
  : boolean;

function useFeatureGuard<T extends Feature | null>(
  featureToCheck: T,
): FeatureGuard<T> {
  const { data, isLoading } = useGetSubscription();

  const isSubscriptionActive = React.useMemo(
    () =>
      !!data?.subscription &&
      (
        [
          SubscriptionStatus.ACTIVE,
          SubscriptionStatus.TRIALING,
        ] as SubscriptionStatus[]
      ).includes(data.subscription.status),
    [data?.subscription],
  );

  const checkAccess = React.useCallback(
    (feature: Feature) => {
      const minimumTier = FEATURE_MINIMUM_TIER[feature] ?? 0;
      const requiredPlanType = FEATURE_PLAN_TYPE[feature] ?? 'ANY';
      const isValidSubscription =
        isSubscriptionActive &&
        (data?.subscription?.plan.tier ?? 0) >= minimumTier &&
        (requiredPlanType === 'ANY' ||
          data?.subscription?.plan.type === requiredPlanType);

      if (isLoading) return false;
      return isValidSubscription;
    },
    [
      data?.subscription?.plan.tier,
      data?.subscription?.plan.type,
      isLoading,
      isSubscriptionActive,
    ],
  );

  if (!featureToCheck) {
    return checkAccess as FeatureGuard<T>;
  }

  return checkAccess(featureToCheck as Feature) as FeatureGuard<T>;
}

export default useFeatureGuard;
